import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
    Column,
    Table,
    useReactTable,
    ColumnFiltersState,
    getCoreRowModel,
    TableOptions,
    ColumnDef,
    SortingState,
    flexRender,
    getFacetedRowModel,
    getFacetedUniqueValues,
    PaginationState
} from '@tanstack/react-table'
import { useDownloadProductsMutation, useGetProductsMutation } from '../redux/api/productsApi'
import { Icon } from '@iconify/react'
import { useQuery } from 'react-query'
import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import PaginationElement from './PaginationElement'
import { useLocation } from 'react-router-dom'
import { commonLabelName } from '../utilities/commonConstants'
import { isValidFile } from '../utilities/helpers'

interface ProductHeaders {
    Name: string
    Version: string
    Platform: string
    Description: string
    'Release Notes': string
    'Install Guide': string
    Downloads: number
    ItemReleaseNotes: string
    ItemInstallNotes: string
    ItemFile: string
    ItemName: string
    ItemDescription: string
    ItemPlatform: string
    ItemVersion: string
    ItemS3Dir: string
}

type Product = {
    ItemCategory: string
    ItemPublished: string
    ItemLatest: string
    ItemName: string
    ItemFile: string
    ItemReleaseNotes: string
    ItemInstallNotes: string
    ItemDescription: string
    ItemExtension: string
    ItemPlatform: string
    ItemVersion: string
    ItemUrl: string
    ItemS3Dir: string
}
interface QueryResponse {
    response: {
        data: Product[]
        totalPages: number
    }
}
const ProductsTable: React.FC<{
    type: string
    columnFilters: ColumnFiltersState
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState | null>>
}> = ({ type, columnFilters, setColumnFilters }) => {
    const useQueryFunction = () => {
        return new URLSearchParams(useLocation().search)
    }
    const query = useQueryFunction()

    const name = query.get('name') || ''
    const version = query.get('version') || ''

    const pageTopRef = useRef<HTMLTableElement | null>(null)
    const tableRef = useRef<HTMLTableElement | null>(null)
    const [getProducts, { isLoading }] = useGetProductsMutation()
    const [downloadProducts] = useDownloadProductsMutation()
    const [sorting, setSorting] = useState<SortingState>([])
    const [totalCount, setTotalCount] = useState<number>(0)

    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25
    })

    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: 0
        }))
    }, [sorting, columnFilters, name, version, type])

    const fetchDataOptions = {
        pageIndex,
        pageSize,
        type,
        sorting,
        columnFilters
    }
    const dataQuery = useQuery<QueryResponse>([type, fetchDataOptions], () => fetchData(fetchDataOptions), {
        keepPreviousData: true,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false
    })
    const itemsPerPage = 25
    const totalItems = totalCount
    const totalPages = Math.ceil(totalItems / itemsPerPage)

    useEffect(() => {
        setSorting([])
    }, [type])

    // Pagination event handlers
    /**
     * Handles page change by updating the table's page index and scrolling to the top of the page.
     *
     * @param {number} pageNumber - The page number to navigate to.
     */
    const handlePageChange = (pageNumber: number) => {
        table.setPageIndex(pageNumber - 1)
        pageTopRef.current?.scrollIntoView()
    }

    /**
     * Navigates to the first page of the table and scrolls to the top.
     */
    const handleFirstPage = () => {
        table.setPageIndex(0)
        pageTopRef.current?.scrollIntoView()
    }
    /**
     * Navigates to the last page of the table and scrolls to the top.
     */
    const handleLastPage = () => {
        table.setPageIndex(table.getPageCount() - 1)
        pageTopRef.current?.scrollIntoView()
    }
    /**
     * Navigates to the previous page of the table and scrolls to the top.
     */
    const handlePrevPage = () => {
        table.previousPage()
        pageTopRef.current?.scrollIntoView()
    }
    /**
     * Navigates to the next page of the table and scrolls to the top.
     */
    const handleNextPage = () => {
        table.nextPage()
        pageTopRef.current?.scrollIntoView()
    }
    /**
     * Fetches data based on the provided options such as pagination, sorting, and filtering.
     *
     * @param {Object} options - The options for fetching data.
     * @param {number} options.pageIndex - The current page index.
     * @param {number} options.pageSize - The number of items per page.
     * @param {string} options.type - The type of items to fetch (e.g., "Current").
     * @param {SortingState} options.sorting - The sorting state, defining which column and direction to sort by.
     * @param {ColumnFiltersState} options.columnFilters - The column filters applied to the data.
     *
     * @returns {Promise<QueryResponse>} A promise resolving to the fetched data including total pages.
     */
    async function fetchData(options: {
        pageIndex: number
        pageSize: number
        type: string
        sorting: SortingState
        columnFilters: ColumnFiltersState
    }): Promise<QueryResponse> {
        try {
            const { pageIndex, pageSize, type } = options
            const params: any = {}

            const sortColumns = sorting.filter((s) => ['ItemName', 'ItemVersion', 'ItemPlatform'].includes(s.id))
            if (sorting.length) {
                const { id, desc } = sortColumns[0]
                params.sortBy = `${id}:${desc ? 'desc' : 'asc'}`
            }
            if (columnFilters.length) {
                columnFilters.forEach((filter) => {
                    params[filter.id] = filter.value
                })
            }
            let itemLatest = type === 'Current' ? 'true' : 'false'

            const result: { data?: any; error?: FetchBaseQueryError | SerializedError } = await getProducts({
                page: pageIndex + 1,
                limit: pageSize,
                ItemLatest: itemLatest,
                ...params
            })

            if (result.data) {
                const responseData = result.data.response
                setTotalCount(responseData?.totalCount || 0)

                const rows = responseData.data || []
                return {
                    response: {
                        data: rows,
                        totalPages: responseData.totalPages || 0
                    }
                }
            } else if (result.error) {
                console.error('Error fetching data:', result.error)
            }

            return {
                response: {
                    data: [],
                    totalPages: 0
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error)
            return {
                response: {
                    data: [],
                    totalPages: 0
                }
            }
        }
    }

    const defaultData = useMemo(() => [], [])

    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize
        }),
        [pageIndex, pageSize]
    )
    /**
     * Initiates the download of a product file from an S3 directory.
     *
     * @param {string} s3Dir - The S3 directory where the file is stored.
     * @param {string} path - The file path within the S3 directory.
     * @param {string} [name] - The name of the file (optional).
     * @param {string} [description] - The description of the file (optional).
     * @param {string} [platform] - The platform associated with the file (optional).
     * @param {string} [version] - The version of the file (optional).
     * @param {string} [fileType] - The file type (optional).
     *
     * @returns {Promise<void>} A promise that resolves when the download process is completed.
     */
    const handleDownload = async (
        s3Dir: string,
        path: string,
        name?: string,
        description?: string,
        platform?: string,
        version?: string,
        fileType?: string,
        fileName?: string
    ) => {
        try {
            // Notify the user that the download has started
            toast.info('Download initiated', {
                autoClose: 2000
            })

            // Request the presigned URL for downloading the file
            const result = await downloadProducts({
                path: `${s3Dir}/${path}`,
                name: name || '',
                description: description || '',
                platform: platform || '',
                version: version || '',
                fileType: fileType || '',
                fileName: fileName || ''
            })
            if ('data' in result && result.data) {
                const presignedUrl = result.data.data
                // Ensure the presignedUrl is valid
                if (!presignedUrl) {
                    console.error('Presigned URL is not available')
                    return
                }
                // Check if the file exists by sending a HEAD request
                const response = await fetch(presignedUrl, { method: 'HEAD' })
                if (response.ok) {
                    const link = document.createElement('a')
                    link.href = presignedUrl
                    link.download = path // Set the desired file name

                    // Append the link to the document
                    document.body.appendChild(link)
                    // Trigger a click event to initiate the download
                    link.click()
                    // Remove the link from the document
                    document.body.removeChild(link)
                } else {
                    toast.error('File not available.')
                }
            }
        } catch (error) {
            console.error('Error downloading product:', error)
            toast.error('Failed to download document.')
        }
    }

    const columns = useMemo<ColumnDef<ProductHeaders, any>[]>(() => {
        const Columns = [
            {
                header: 'Name',
                accessorKey: 'ItemName',
                size: (tableRef?.current?.clientWidth || 780) * 0.1,
                canSort: true
            },
            {
                header: 'Version',
                accessorKey: 'ItemVersion',
                size:
                    tableRef?.current?.clientWidth && tableRef.current.clientWidth > 1210
                        ? (tableRef.current.clientWidth || 780) * 0.1
                        : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 1150
                          ? (tableRef?.current?.clientWidth || 780) * 0.09
                          : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 940
                            ? (tableRef?.current?.clientWidth || 780) * 0.08
                            : tableRef?.current?.clientWidth && tableRef.current.clientWidth < 939
                              ? (tableRef?.current?.clientWidth || 780) * 0.07
                              : undefined,
                canSort: true
            },
            {
                header: 'Platform',
                accessorKey: 'ItemPlatform',
                size:
                    tableRef?.current?.clientWidth && tableRef.current.clientWidth < 1210
                        ? (tableRef.current.clientWidth || 780) * 0.1
                        : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 1150
                          ? (tableRef?.current?.clientWidth || 780) * 0.1
                          : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 940
                            ? (tableRef?.current?.clientWidth || 780) * 0.09
                            : tableRef?.current?.clientWidth && tableRef.current.clientWidth < 939
                              ? (tableRef?.current?.clientWidth || 780) * 0.08
                              : undefined,
                canSort: true
            },
            {
                header: 'Description',
                accessorKey: 'ItemDescription',
                size:
                    tableRef?.current?.clientWidth && tableRef.current.clientWidth < 1210
                        ? (tableRef.current.clientWidth || 780) * 0.4
                        : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 1150
                          ? (tableRef.current.clientWidth || 780) * 0.4
                          : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 940
                            ? (tableRef.current.clientWidth || 780) * 0.4
                            : tableRef?.current?.clientWidth && tableRef.current.clientWidth < 939
                              ? (tableRef.current.clientWidth || 780) * 0.38
                              : undefined,
                canSort: false,
                enableSorting: false,
                enableColumnFilter: false
            },
            {
                header: 'Release Notes',
                accessorKey: 'ItemReleaseNotes',
                size: (tableRef?.current?.clientWidth || 780) * 0.1,
                enableColumnFilter: false,
                enableSorting: false,
                canSort: false,
                cell: (cell: any) => {
                    const file = cell.row.original.ItemReleaseNotes
                    if (!isValidFile(file)) return null
                    return (
                        <button
                            className='btn bg-none'
                            onClick={() => {
                                const { ItemReleaseNotes, ItemS3Dir, ItemName, ItemDescription, ItemPlatform, ItemVersion, ItemFile } =
                                    cell.row.original
                                handleDownload(
                                    ItemS3Dir,
                                    ItemReleaseNotes,
                                    ItemName,
                                    ItemDescription,
                                    ItemPlatform,
                                    ItemVersion,
                                    'Release Notes',
                                    ItemFile
                                )
                            }}
                        >
                            <Icon icon='material-symbols-light:download' width={35} height={35} className=' text-primary' />
                        </button>
                    )
                }
            },
            {
                header: 'Install Guide',
                accessorKey: 'ItemInstallNotes',
                size: (tableRef?.current?.clientWidth || 780) * 0.1,
                enableColumnFilter: false,
                enableSorting: false,
                canSort: false,
                cell: (cell: any) => {
                    const file = cell.row.original.ItemInstallNotes
                    if (!isValidFile(file)) return null

                    return (
                        <button
                            className='btn bg-none'
                            onClick={() => {
                                const { ItemInstallNotes, ItemS3Dir, ItemName, ItemDescription, ItemPlatform, ItemVersion, ItemFile } =
                                    cell.row.original
                                handleDownload(
                                    ItemS3Dir,
                                    ItemInstallNotes,
                                    ItemName,
                                    ItemDescription,
                                    ItemPlatform,
                                    ItemVersion,
                                    'Install Guide',
                                    ItemFile
                                )
                            }}
                        >
                            <Icon icon='material-symbols-light:download' width={35} height={35} className='text-primary' />
                        </button>
                    )
                }
            },
            {
                header: 'Download',
                accessorKey: 'ItemFile',
                size:
                    tableRef?.current?.clientWidth && tableRef.current.clientWidth > 1210
                        ? (tableRef.current.clientWidth || 780) * 0.1
                        : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 1150
                          ? (tableRef?.current?.clientWidth || 780) * 0.11
                          : tableRef?.current?.clientWidth && tableRef.current.clientWidth > 940
                            ? (tableRef?.current?.clientWidth || 780) * 0.13
                            : tableRef?.current?.clientWidth && tableRef.current.clientWidth < 939
                              ? (tableRef?.current?.clientWidth || 780) * 0.17
                              : undefined,
                canSort: false,
                enableSorting: false,
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const file = cell.row.original.ItemFile
                    console.log(file, 'cell.row.original.ItemFile')
                    if (!isValidFile(file)) return null
                    return (
                        <button
                            className='btn btn-primary flex items-center bg-primary text-xs text-white rounded-md w-fit px-2 py-1'
                            onClick={() => {
                                const { ItemS3Dir, ItemFile, ItemName, ItemDescription, ItemPlatform, ItemVersion } = cell.row.original
                                handleDownload(
                                    ItemS3Dir,
                                    ItemFile,
                                    ItemName,
                                    ItemDescription,
                                    ItemPlatform,
                                    ItemVersion,
                                    'Software',
                                    ItemFile
                                )
                            }}
                        >
                            Download
                            <Icon icon='material-symbols-light:download' className='w-6 h-6' />
                        </button>
                    )
                }
            }
        ]

        return Columns // eslint-disable-next-line
    }, [tableRef?.current?.clientWidth])

    const table = useReactTable({
        data: dataQuery.data?.response?.data || defaultData,
        columns,
        state: {
            sorting,
            columnFilters,
            pagination
        },
        onPaginationChange: setPagination,
        pageCount: dataQuery.data?.response?.totalPages || 0,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        onSortingChange: (newSorting) => {
            setSorting(newSorting)
            setPagination((prev) => ({
                ...prev,
                pageIndex: 0
            }))
        },
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues()
    } as TableOptions<any>)

    return (
        <>
            <div className='container mx-auto py-8' ref={pageTopRef}>
                <div className='bg-white p-4 rounded-lg shadow-lg'>
                    <div className='border border-gray rounded-lg p-4'>
                        <div className='col-span-12'>
                            <div className='relative overflow-x-auto table-scroll' style={{ maxHeight: '500px' }}>
                                <table className='w-full overflow-auto' style={{ minWidth: 780 }} ref={tableRef}>
                                    <thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <tr key={headerGroup.id} className='align-baseline text-left'>
                                                {headerGroup.headers.map((header) => (
                                                    <th
                                                        key={header.id}
                                                        colSpan={header.colSpan}
                                                        className={`text-primary p-3 pt-0 bg-white sticky top-0 z-10 border-b border-gray`}
                                                        style={{ width: header.column.getSize() }}
                                                    >
                                                        {header.isPlaceholder ? null : (
                                                            <>
                                                                <div
                                                                    {...{
                                                                        className: header.column.getCanSort()
                                                                            ? 'cursor-pointer select-none flex items-center'
                                                                            : '',
                                                                        onClick: header.column.getCanSort()
                                                                            ? header.column.getToggleSortingHandler()
                                                                            : undefined
                                                                    }}
                                                                >
                                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                                    {header.column.getCanSort() ? (
                                                                        <div>
                                                                            {{
                                                                                asc: <Icon icon='majesticons:chevron-up' />,
                                                                                desc: <Icon icon='majesticons:chevron-down' />
                                                                            }[header.column.getIsSorted() as string] ?? null}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                                {header.column.getCanFilter() ? (
                                                                    <div>
                                                                        <Filter column={header.column} table={table} />
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    {isLoading || dataQuery?.isLoading ? (
                                        <tbody>
                                            <tr>
                                                {Array.from({ length: 7 }).map((_, index) => (
                                                    <td key={index} className='p-3'>
                                                        <Skeleton count={3} />
                                                    </td>
                                                ))}
                                            </tr>
                                        </tbody>
                                    ) : (
                                        <tbody className='text-left border-t border-gray'>
                                            {dataQuery.data?.response?.data?.length ? (
                                                table.getRowModel().rows.map((row, index) => (
                                                    <tr
                                                        key={row.id}
                                                        className={`p-3 ${
                                                            index === table.getRowModel().rows.length - 1 ? '' : 'border-b border-gray'
                                                        }`}
                                                    >
                                                        {row.getVisibleCells().map((cell: any) => (
                                                            <td
                                                                key={cell.id}
                                                                className='p-3'
                                                                style={{ width: cell.column.getSize(), wordBreak: 'break-word' }}
                                                            >
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={columns.length} className='p-3 text-center'>
                                                        <h1 className='text-bold'>{commonLabelName?.noRecordFound}</h1>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>
                    {dataQuery.data?.response?.data?.length && !dataQuery.isLoading && !isLoading && totalCount > 25 ? (
                        <div className='flex items-center justify-between gap-2 mt-2 px-4 py-3 font-semibold'>
                            <div className='text-sm text-[#2D3748]'>
                                Showing{' '}
                                {Math.min(table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1, totalCount)} to{' '}
                                {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, totalCount)}{' '}
                                of {totalCount} entries
                            </div>
                            <div className='flex'>
                                <PaginationElement
                                    currentPage={pageIndex + 1}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    onFirstPage={handleFirstPage}
                                    onLastPage={handleLastPage}
                                    onPrevPage={handlePrevPage}
                                    onNextPage={handleNextPage}
                                />
                            </div>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        </>
    )
}

function Filter({ column, table }: { column: Column<any, unknown>; table: Table<any> }) {
    const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id)

    const columnFilterValue = column.getFilterValue()

    const sortedUniqueValues = useMemo(
        () => (typeof firstValue === 'number' ? [] : Array.from(column.getFacetedUniqueValues().keys()).sort()),
        // eslint-disable-next-line
        [column.getFacetedUniqueValues()]
    )

    return typeof firstValue === 'number' ? (
        <div>
            <div className='flex space-x-2'>
                <DebouncedInput
                    type='number'
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    value={(columnFilterValue as [number, number])?.[0] ?? ''}
                    onChange={(value) => column.setFilterValue((old: [number, number]) => [value, old?.[1]])}
                    placeholder={`Min ${column.getFacetedMinMaxValues()?.[0] ? `(${column.getFacetedMinMaxValues()?.[0]})` : ''}`}
                    style={{ maxWidth: column.getSize() }}
                    className='border border-light shadow-sm text-black text-xs rounded-md w-full pl-9 px-2 py-2 font-normal placeholder:text-light focus:outline-none'
                />
            </div>
            <div className='h-1' />
        </div>
    ) : (
        <>
            <datalist id={column.id + 'list'}>
                {sortedUniqueValues.slice(0, 5000).map((value: any) => (
                    <option value={value} key={value} />
                ))}
            </datalist>
            <div className='relative flex items-center mt-2'>
                <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                    <Icon icon='iconamoon:search' className='text-light w-4 h-4' />
                </div>
                <DebouncedInput
                    type='text'
                    value={(columnFilterValue ?? '') as string}
                    onChange={(value) => column.setFilterValue(value)}
                    placeholder={`Search`}
                    className='border border-light text-black text-xs rounded-md md:w-[120px] w-full pl-9 px-2 py-2 font-normal placeholder:text-light focus:outline-none'
                    style={{ maxWidth: column.getSize() }}
                    // list={column.id + 'list'}
                />
            </div>
            <div className='h-1' />
        </>
    )
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & React.InputHTMLAttributes<HTMLInputElement>) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line
    }, [value])

    return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}

export default ProductsTable